import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { CrematoryListInfo } from '@app/objects/Crematory';
import { ServiceType } from '@app/objects/Pet';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';

export interface ServiceTypeOption {
	label: string;
	value: ServiceType;
}

function toServiceTypes(crematory?: CrematoryListInfo): Array<ServiceType> {
	const result: Array<ServiceType> = [];

	if (crematory?.private?.useServiceType) {
		result.push(ServiceType.Private);
	}

	if (crematory?.semiPrivate?.useServiceType) {
		result.push(ServiceType.SemiPrivate);
	}

	if (crematory?.communal?.useServiceType) {
		result.push(ServiceType.Communal);
	}

	if (crematory?.storage?.useServiceType) {
		result.push(ServiceType.Storage);
	}

	if (crematory?.productsOnly?.useServiceType) {
		result.push(ServiceType.ProductsOnly);
	}

	return result;
}

function getTranslationMap(isAustralia: boolean): Map<ServiceType, string> {
	return new Map<ServiceType, string>([
		[ServiceType.Private, `enums.service-type.${isAustralia ? 'aus-' : ''}private`],
		[ServiceType.SemiPrivate, `enums.service-type.${isAustralia ? 'aus-' : ''}semiprivate`],
		[ServiceType.Communal, 'enums.service-type.communal'],
		[ServiceType.Storage, 'enums.service-type.storage'],
		[ServiceType.ProductsOnly, 'enums.service-type.products-only'],
	]);
}

export function useServiceTypes(crematoryId?: number): Array<ServiceTypeOption> {
	const crematory = useCrematory(crematoryId);
	const { t } = useTranslation();

	const map = React.useMemo(() => getTranslationMap(crematory?.country?.toLowerCase() === 'aus'), [crematory?.country]);

	return React.useMemo(() => toServiceTypes(crematory).map((type: ServiceType) => ({
		value: type,
		label: t(map.get(type)!),
	})), [crematory, map]);
}
